/**
 * Pacing Module
 *
 * Calculates word display duration based on WPM and punctuation.
 * Deterministic: same inputs always produce same timing.
 */

import { endsWithSentenceEnd, endsWithClausePunctuation } from './tokenization';

/**
 * Pacing configuration constants
 */
export const PACING_CONFIG = {
  /** Minimum WPM allowed */
  WPM_MIN: 100,
  /** Maximum WPM allowed */
  WPM_MAX: 900,
  /** Default WPM */
  WPM_DEFAULT: 700,

  /** Extra pause for comma, semicolon, colon (ms) */
  CLAUSE_PAUSE_MS: 80,
  /** Extra pause for sentence end .?! (ms) */
  SENTENCE_PAUSE_MS: 220,

  /** Number of words to rewind */
  REWIND_WORDS: 10,
  /** Number of words to fast-forward */
  FAST_FORWARD_WORDS: 10,

  /** WPM adjustment step for keyboard/controller */
  WPM_STEP: 25,
} as const;

/**
 * Clamp WPM to valid range.
 *
 * @param wpm - The WPM value to clamp
 * @returns WPM clamped to [WPM_MIN, WPM_MAX]
 */
export function clampWpm(wpm: number): number {
  return Math.max(PACING_CONFIG.WPM_MIN, Math.min(PACING_CONFIG.WPM_MAX, wpm));
}

/**
 * Calculate base word duration in milliseconds from WPM.
 *
 * @param wpm - Words per minute
 * @returns Duration in milliseconds for one word
 */
export function getBaseWordDurationMs(wpm: number): number {
  const clampedWpm = clampWpm(wpm);
  // 60,000 ms per minute / WPM = ms per word
  return 60_000 / clampedWpm;
}

/**
 * Calculate the total display duration for a word, including punctuation pauses.
 *
 * @param word - The word to calculate duration for
 * @param wpm - Words per minute
 * @param punctuationPauses - Whether to apply punctuation pauses
 * @returns Total duration in milliseconds
 */
export function getWordDurationMs(
  word: string,
  wpm: number,
  punctuationPauses: boolean = true
): number {
  let duration = getBaseWordDurationMs(wpm);

  if (punctuationPauses && word) {
    if (endsWithSentenceEnd(word)) {
      duration += PACING_CONFIG.SENTENCE_PAUSE_MS;
    } else if (endsWithClausePunctuation(word)) {
      duration += PACING_CONFIG.CLAUSE_PAUSE_MS;
    }
  }

  return duration;
}

/**
 * Calculate the new word index after rewinding.
 *
 * @param currentIndex - Current word index
 * @param wordCount - Total number of words
 * @param rewindAmount - Number of words to rewind (default: REWIND_WORDS)
 * @returns New index, clamped to valid range
 */
export function calculateRewindIndex(
  currentIndex: number,
  wordCount: number,
  rewindAmount: number = PACING_CONFIG.REWIND_WORDS
): number {
  if (wordCount === 0) return 0;
  return Math.max(0, currentIndex - rewindAmount);
}

/**
 * Calculate the new word index after fast-forwarding.
 *
 * @param currentIndex - Current word index
 * @param wordCount - Total number of words
 * @param forwardAmount - Number of words to fast-forward (default: FAST_FORWARD_WORDS)
 * @returns New index, clamped to valid range
 */
export function calculateFastForwardIndex(
  currentIndex: number,
  wordCount: number,
  forwardAmount: number = PACING_CONFIG.FAST_FORWARD_WORDS
): number {
  if (wordCount === 0) return 0;
  const lastIndex = wordCount - 1;
  return Math.min(lastIndex, currentIndex + forwardAmount);
}

/**
 * Check if the current index is at the last word.
 *
 * @param currentIndex - Current word index
 * @param wordCount - Total number of words
 * @returns true if at the last word
 */
export function isAtEnd(currentIndex: number, wordCount: number): boolean {
  if (wordCount === 0) return true;
  return currentIndex >= wordCount - 1;
}

/**
 * Check if the current index is at the first word.
 *
 * @param currentIndex - Current word index
 * @returns true if at the first word
 */
export function isAtStart(currentIndex: number): boolean {
  return currentIndex <= 0;
}
